import { useEffect, useMemo, useState } from 'react';
import Icon from './Icon';
import {
  buildSlotEditorDraft,
  buildWeekWorkshopModel,
} from '../services/weekWorkshop';

function RecipeDetails({ meal, onGenerateRecipe, generatingRecipeKey, disabled }) {
  const isGenerating = generatingRecipeKey === meal.recipeKey;
  if (!meal.recipeReady) {
    return (
      <button
        type="button"
        className="week-workshop__text-action"
        onClick={() => onGenerateRecipe?.(meal)}
        disabled={disabled || !onGenerateRecipe || isGenerating || meal.needsReview}
      >
        {isGenerating ? 'Génération…' : 'Générer cette recette'}
      </button>
    );
  }

  return (
    <details className="week-workshop__details">
      <summary>Ouvrir la recette</summary>
      <div className="week-workshop__recipe">
        <strong>Ingrédients</strong>
        <ul>
          {(meal.recipe?.ingredients || []).map((ingredient, index) => (
            <li key={`${ingredient}-${index}`}>{ingredient}</li>
          ))}
        </ul>
        <strong>Préparation</strong>
        <ol>
          {(meal.recipe?.preparation || []).map((step, index) => (
            <li key={`${step}-${index}`}>{step}</li>
          ))}
        </ol>
        {onGenerateRecipe && (
          <button
            type="button"
            className="week-workshop__text-action"
            onClick={() => onGenerateRecipe(meal)}
            disabled={disabled || isGenerating}
          >
            {isGenerating ? 'Régénération…' : 'Régénérer uniquement cette recette'}
          </button>
        )}
      </div>
    </details>
  );
}

function AlternativesDetails({ meal, onGenerateRecipe, generatingRecipeKey, disabled }) {
  if (meal.alternativesCount === 0) return null;
  return (
    <details className="week-workshop__details">
      <summary>{meal.alternativesCount} alternative{meal.alternativesCount > 1 ? 's' : ''}</summary>
      <div className="week-workshop__alternatives">
        {meal.alternatives.map((alternative) => {
          const candidate = {
            ...alternative,
            slot: meal.slot,
            slotLabel: meal.slotLabel,
            recipeKey: `${meal.slot}::${alternative.title.trim().toLowerCase().replace(/\s+/g, ' ')}`,
          };
          const isGenerating = generatingRecipeKey === candidate.recipeKey;
          return (
            <div className="week-workshop__alternative" key={`${meal.slot}-${alternative.id}-${alternative.title}`}>
              <div>
                <strong>{alternative.title}</strong>
                {alternative.hint && <p>{alternative.hint}</p>}
              </div>
              <span className={`week-workshop__mini-state ${alternative.recipeReady ? 'is-ready' : 'is-missing'}`}>
                {alternative.recipeReady ? 'Recette prête' : 'Recette manquante'}
              </span>
              {!alternative.recipeReady && onGenerateRecipe && (
                <button
                  type="button"
                  className="week-workshop__text-action"
                  onClick={() => onGenerateRecipe(candidate)}
                  disabled={disabled || isGenerating}
                >
                  {isGenerating ? 'Génération…' : 'Générer'}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </details>
  );
}

function SlotEditor({ draft, saving, error, onCancel, onSave }) {
  const [title, setTitle] = useState(draft.title);
  const [alternatives, setAlternatives] = useState(draft.alternatives);

  useEffect(() => {
    setTitle(draft.title);
    setAlternatives(draft.alternatives);
  }, [draft]);

  const updateAlternative = (index, value) => {
    setAlternatives((current) => current.map((alternative, itemIndex) => (
      itemIndex === index ? { ...alternative, title: value } : alternative
    )));
  };

  return (
    <section className="week-workshop__editor" aria-label={`Modifier ${draft.slotLabel}`}>
      <div className="week-workshop__editor-head">
        <div>
          <span className="week-workshop__eyebrow">Modification ciblée</span>
          <h4>{draft.slotLabel}</h4>
        </div>
        <button type="button" className="week-workshop__close" onClick={onCancel} disabled={saving} aria-label="Fermer l’éditeur">
          ×
        </button>
      </div>
      <p className="week-workshop__editor-note">
        Ce menu de référence alimente ce créneau dans la semaine et les étapes suivantes.
      </p>
      <label className="week-workshop__field">
        <span>Titre du repas</span>
        <input value={title} onChange={(event) => setTitle(event.target.value)} disabled={saving} />
      </label>
      {alternatives.length > 0 && (
        <div className="week-workshop__field-group">
          <span>Alternatives existantes</span>
          {alternatives.map((alternative, index) => (
            <input
              key={`${draft.slot}-alternative-${index}`}
              value={alternative.title}
              onChange={(event) => updateAlternative(index, event.target.value)}
              disabled={saving}
              aria-label={`${draft.slotLabel} · alternative ${index + 1}`}
            />
          ))}
        </div>
      )}
      {error && <p className="week-workshop__error" role="alert">{error}</p>}
      <div className="week-workshop__editor-actions">
        <button type="button" className="jrn-btn jrn-btn--ghost" onClick={onCancel} disabled={saving}>Annuler</button>
        <button
          type="button"
          className="jrn-btn jrn-btn--primary"
          onClick={() => onSave({ ...draft, title, alternatives })}
          disabled={saving || !title.trim()}
        >
          {saving ? 'Enregistrement…' : 'Enregistrer ce créneau'}
        </button>
      </div>
    </section>
  );
}

export default function ClientWeekWorkshop({
  weekMeals,
  saving = false,
  savedAt = null,
  error = null,
  disabled = false,
  generatingRecipeKey = null,
  onSaveSlot,
  onGenerateRecipe,
  onOpenClientPreview,
}) {
  const model = useMemo(() => buildWeekWorkshopModel(weekMeals), [weekMeals]);
  const [editingSlot, setEditingSlot] = useState(null);
  const editorDraft = useMemo(
    () => (editingSlot ? buildSlotEditorDraft(weekMeals, editingSlot) : null),
    [editingSlot, weekMeals],
  );

  useEffect(() => {
    if (editingSlot && !editorDraft) setEditingSlot(null);
  }, [editingSlot, editorDraft]);

  if (model.dayCount === 0) {
    return (
      <div className="week-workshop week-workshop--empty">
        <Icon type="calendar" size={20} />
        <h3>La semaine cliente n’est pas encore disponible</h3>
        <p>Enregistre d’abord une structure alimentaire reconnue dans le plan.</p>
      </div>
    );
  }

  return (
    <section className="week-workshop" aria-labelledby="week-workshop-title">
      <header className="week-workshop__hero">
        <div>
          <span className="week-workshop__eyebrow">Vue réellement utilisée par l’application</span>
          <h3 id="week-workshop-title">La semaine de la cliente</h3>
          <p>Scanne les sept jours, repère les répétitions et corrige un créneau sans régénérer tout le programme.</p>
        </div>
        <button
          type="button"
          className="jrn-btn jrn-btn--soft"
          onClick={onOpenClientPreview}
          disabled={disabled || !onOpenClientPreview}
        >
          Ouvrir l’aperçu cliente
        </button>
      </header>

      <div className="week-workshop__summary" aria-label="Résumé de la semaine">
        <span><strong>{model.dayCount}</strong> jours</span>
        <span><strong>{model.mealCount}</strong> repas affichés</span>
        <span><strong>{model.readyRecipeCount}</strong> recettes prêtes</span>
        {model.repeatedMealCount > 0 && <span><strong>{model.repeatedMealCount}</strong> repas répétés</span>}
        {savedAt && <span className="week-workshop__saved"><Icon type="check" size={12} /> Enregistré</span>}
      </div>

      {disabled && (
        <p className="week-workshop__notice" role="status">
          La dernière modification du plan doit finir de s’enregistrer avant d’éditer la semaine.
        </p>
      )}
      {error && !editorDraft && <p className="week-workshop__error" role="alert">{error}</p>}

      {editorDraft && (
        <SlotEditor
          draft={editorDraft}
          saving={saving}
          error={error}
          onCancel={() => setEditingSlot(null)}
          onSave={async (nextDraft) => {
            try {
              await onSaveSlot?.(nextDraft);
              setEditingSlot(null);
            } catch {
              // Le parent affiche l’erreur et l’éditeur reste ouvert pour corriger.
            }
          }}
        />
      )}

      <div className="week-workshop__days">
        {model.days.map((day) => (
          <article className="week-workshop__day" key={day.id}>
            <header>
              <span>Jour {day.index}</span>
              <h4>{day.label}</h4>
            </header>
            <div className="week-workshop__meals">
              {day.meals.length === 0 && <p className="week-workshop__empty-day">Aucun repas</p>}
              {day.meals.map((meal) => (
                <section className={`week-workshop__meal ${meal.needsReview ? 'is-review' : ''}`} key={meal.id}>
                  <div className="week-workshop__meal-head">
                    <span>{meal.slotLabel}</span>
                    <button
                      type="button"
                      className="week-workshop__edit"
                      onClick={() => setEditingSlot(meal.slot)}
                      disabled={disabled || saving || !onSaveSlot || meal.needsReview}
                    >
                      Modifier
                    </button>
                  </div>
                  <h5>{meal.title || 'Repas à revoir'}</h5>
                  <div className="week-workshop__states">
                    <span className={`week-workshop__state ${meal.recipeReady ? 'is-ready' : 'is-missing'}`}>
                      {meal.recipeReady ? 'Recette prête' : 'Recette à générer'}
                    </span>
                    {meal.occurrenceCount > 1 && (
                      <span className="week-workshop__state is-repeat">Utilisé {meal.occurrenceCount}×</span>
                    )}
                    {meal.needsReview && <span className="week-workshop__state is-review">À revoir</span>}
                  </div>
                  <div className="week-workshop__meal-actions">
                    <AlternativesDetails
                      meal={meal}
                      onGenerateRecipe={onGenerateRecipe}
                      generatingRecipeKey={generatingRecipeKey}
                      disabled={disabled || saving}
                    />
                    <RecipeDetails
                      meal={meal}
                      onGenerateRecipe={onGenerateRecipe}
                      generatingRecipeKey={generatingRecipeKey}
                      disabled={disabled || saving}
                    />
                  </div>
                </section>
              ))}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
